import { Injectable } from '@angular/core';
import { SpaceRepository } from '../state/space.repository';
import { PresenceProvider, PresenceRoomInfo } from './presence-provider';

/** Every surface that holds presence rooms for spaces the user is *not* inside. */
export enum PresenceRoomOwner {
  SWITCHER = 'switcher',
  SPACES_LIST = 'spaces-list',
}

interface HeldRoom {
  info: PresenceRoomInfo;
  owners: Set<PresenceRoomOwner>;
}

/**
 * Ref-counted ownership of space presence rooms.
 *
 * `PresenceProvider.leaveSpacePresenceRooms` is not ref-counted: it leaves the socket room *and*
 * blanks the local presence store for those spaces. With more than one list-style consumer that
 * means whichever one unmounts first silently destroys the other's data — the spaces list page
 * unmounting would kill the sidebar switcher's indicators for every overlapping space.
 *
 * Only for list-style consumers; the in-space session presence path still talks to PresenceProvider
 * directly.
 */
@Injectable({ providedIn: 'root' })
export class SpacePresenceRoomsService {
  private readonly heldRooms = new Map<string, HeldRoom>();

  constructor(
    private presenceProvider: PresenceProvider,
    private spaceRepository: SpaceRepository,
  ) {}

  /**
   * Claim presence rooms for an owner. Idempotent and additive — safe to call with the owner's full
   * current set on every page of a paginated list.
   */
  acquire(owner: PresenceRoomOwner, rooms: PresenceRoomInfo[]): void {
    // Per-owner rather than "new to the map": a second owner claiming a room the first already
    // holds still needs its own snapshot, since it can't know whether the first owner's query landed.
    const newForOwner: PresenceRoomInfo[] = [];

    for (const room of rooms) {
      const held = this.heldRooms.get(room.spaceId);
      if (!held) {
        this.heldRooms.set(room.spaceId, { info: room, owners: new Set([owner]) });
        newForOwner.push(room);
      } else if (!held.owners.has(owner)) {
        held.owners.add(owner);
        newForOwner.push(held.info);
      }
    }

    if (!newForOwner.length) {
      return;
    }

    const spaceIds = newForOwner.map((room) => room.spaceId);
    this.presenceProvider
      .joinSpacePresenceRooms(spaceIds, newForOwner)
      // Query only once the join is acked, matching joinAndQuerySpacePresenceRooms.
      .then(() => {
        this.presenceProvider.querySpacePresenceRooms(spaceIds, newForOwner);
      })
      .catch(() => {
        // Give the rooms back so the owner's next acquire retries them, rather than leaving them
        // marked held-but-never-queried until a reload.
        this.abandon(owner, spaceIds);
      });
  }

  /** Release specific spaces for an owner. Rooms still held by another owner are kept. */
  release(owner: PresenceRoomOwner, spaceIds: string[]): void {
    const releasable: PresenceRoomInfo[] = [];

    for (const spaceId of spaceIds) {
      const held = this.heldRooms.get(spaceId);
      if (!held || !held.owners.delete(owner) || held.owners.size) {
        continue;
      }
      this.heldRooms.delete(spaceId);

      // The session owns the active space's membership independently; emitting a leave for it would
      // blank the presence the space itself is rendering.
      if (spaceId === this.spaceRepository.activeSpaceId) {
        continue;
      }
      releasable.push(held.info);
    }

    if (releasable.length) {
      this.presenceProvider.leaveSpacePresenceRooms(
        releasable.map((room) => room.spaceId),
        releasable,
      );
    }
  }

  /**
   * Give up ownership without emitting a leave, for a space the user is navigating *into* — the
   * session is about to join and own that room itself.
   */
  abandon(owner: PresenceRoomOwner, spaceIds: string[]): void {
    for (const spaceId of spaceIds) {
      const held = this.heldRooms.get(spaceId);
      if (held?.owners.delete(owner) && !held.owners.size) {
        this.heldRooms.delete(spaceId);
      }
    }
  }

  /** Release everything an owner holds. */
  releaseAll(owner: PresenceRoomOwner): void {
    const owned: string[] = [];
    this.heldRooms.forEach((held, spaceId) => {
      if (held.owners.has(owner)) {
        owned.push(spaceId);
      }
    });
    this.release(owner, owned);
  }

  /**
   * Whether any owner still holds this space's room. For the in-space presence path, which leaves
   * the space being switched away from — that leave also clears the space's stored presence, which
   * a list-style surface still rendering it needs to keep.
   */
  isHeld(spaceId: string): boolean {
    return this.heldRooms.has(spaceId);
  }
}
